import React, { useState, useEffect } from "react";
import {
    Box,
    Modal,
    Fade,
    Typography,
    IconButton,
    Button,
    TextField,
    Autocomplete,
    Rating,
    Paper,
    Divider,
    Stack,
    CircularProgress,
    Checkbox,
    Chip,
    Collapse,
    Tooltip,
    alpha,
    InputBase
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import StarIcon from "@mui/icons-material/Star";
import SendIcon from "@mui/icons-material/Send";
import LinkIcon from "@mui/icons-material/Link";
import TagIcon from "@mui/icons-material/Tag";
import CommentIcon from "@mui/icons-material/Comment";
import DriveFileRenameOutlineIcon from "@mui/icons-material/DriveFileRenameOutline";
import FavoriteIcon from "@mui/icons-material/Favorite";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import { API_URL, popupTimer, INPUT_LIMITS } from "../../consts";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import { toggleLike } from "../../services/likeService";
import MenuIcon from "@mui/icons-material/Menu";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import FolderIcon from '@mui/icons-material/Folder';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SearchIcon from "@mui/icons-material/Search";

// 1) Import useLocation
import { useLocation } from "react-router-dom";

import ImageUploaderAndCrop from "../Space/ImageUploaderAndCrop";

// Helper to recursively flatten nested spaces using "childrens"
const flattenSpaces = (spaceList = []) => {
    let result = [];
    for (const space of spaceList) {
        result.push(space);
        if (space.childrens && space.childrens.length > 0) {
            result = result.concat(flattenSpaces(space.childrens));
        }
    }
    return result;
};

// Helper to find a space in the tree
const findSpaceInTree = (spaces, targetId) => {
    for (const space of spaces) {
        if (space.id === targetId) {
            return space;
        }
        if (space.childrens && space.childrens.length > 0) {
            const found = findSpaceInTree(space.childrens, targetId);
            if (found) return found;
        }
    }
    return null;
};

const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 550,
    maxWidth: "95%",
    height: "90vh",
    display: "flex",
    flexDirection: "column",
    bgcolor: "background.paper",
    boxShadow: 24,
    borderRadius: 3,
    p: 0,
};

// Add this custom style object near the top of the file
const spaceSelectionStyles = {
    chip: {
        transition: 'all 0.2s ease',
        '&:hover': {
            bgcolor: 'primary.light',
            color: 'primary.contrastText',
            '& .MuiChip-deleteIcon': {
                color: 'inherit',
                opacity: 1
            }
        },
        '& .MuiChip-deleteIcon': {
            color: 'inherit',
            opacity: 0.7,
            transition: 'opacity 0.2s ease',
            '&:hover': {
                color: 'inherit',
                opacity: 1
            }
        }
    },
    spaceItem: {
        transition: 'all 0.2s ease',
        '&:hover': {
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08)
        }
    },
    selectedSpaceItem: {
        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
        '&:hover': {
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.16)
        }
    }
};

const RatingObject = ({
    isFeedback,
    id,
    rateId,
    open,
    handleClose,
    onRateSuccessfully,
    initialData, // Add this new prop for pre-populating data
    preselectedSpaceId, // Add this new prop for pre-selected space
    preselectedParentSpaceId // Add this new prop for parent space ID
}) => {
    const dispatch = useDispatch();
    const { selectedSpaceId, selectedObjectId } = useSelector(state => ({
        selectedSpaceId: state.selectedSpaceId,
        selectedObjectId: state.selectedObjectId
    }));

    // 2) Get current location from React Router
    const location = useLocation();

    // -----------------------------------------------
    // Local State for Rating / Feedback
    // -----------------------------------------------
    const [ratingObject, setRatingObject] = useState(initialData?.average_stars || 0);
    const [title, setTitle] = useState(initialData?.name || initialData?.title || "");
    const [comment, setComment] = useState(initialData?.comment || initialData?.content || initialData?.description || "");
    const [tags, setTags] = useState(initialData?.keywords || initialData?.tags || []);
    const [linkUrl, setLinkUrl] = useState(initialData?.page_id?.href || initialData?.url || initialData?.linkUrl || "");
    const [isPublic, setIsPublic] = useState(initialData?.is_public || false);

    // Debug logging
    useEffect(() => {
    }, [initialData, preselectedSpaceId]);

    // -----------------------------------------------
    // Local State for Image Handling
    // -----------------------------------------------
    const [displayedImage, setDisplayedImage] = useState(initialData?.image_url || null);
    const [stagedImageFile, setStagedImageFile] = useState(null);
    const [isDragging, setIsDragging] = useState(false);

    // -----------------------------------------------
    // Spaces (fetching & selection)
    // -----------------------------------------------
    const [spacesList, setSpacesList] = useState([]);
    const [selectedSpaces, setSelectedSpaces] = useState([]);
    const [spacesLoading, setSpacesLoading] = useState(false);
    const [expandedSpaces, setExpandedSpaces] = useState({});
    const [loadingSubspaces, setLoadingSubspaces] = useState({});
    const [showSpaceSelection, setShowSpaceSelection] = useState(false);
    
    // Store the original full spaces list separately from search results
    const [originalSpacesList, setOriginalSpacesList] = useState([]);
    
    // -----------------------------------------------
    // Search State
    // -----------------------------------------------
    const [searchQuery, setSearchQuery] = useState("");
    const [isSearching, setIsSearching] = useState(false);
    useEffect(() => {
    }, [initialData]);

    // -----------------------------------------------
    // Unified Spaces API Handler with Selection Preservation
    // -----------------------------------------------
    const updateSpacesListWithPreservation = async (url, preserveSelected = true, isSearch = false) => {
        try {
            // If we're searching with a space_name, make a single call without shared parameter
            if (url.includes('space_name=')) {
                // Add is_add_mode parameter
                const searchUrl = url.includes('?') ? url + '&is_add_mode=true' : url + '?is_add_mode=true';
                
                const response = await axios.get(searchUrl);
                const spaces = response.data.spaces || [];
                
                // Update states
                if (isSearch) {
                    setSpacesList(spaces);
                } else {
                    setOriginalSpacesList(spaces);
                    setSpacesList(spaces);
                }
                return;
            }
            
            // For non-search calls, make two separate calls for owned and shared
            const ownUrl = url.includes('shared=') 
                ? url.replace('&shared=true', '&shared=false').replace('&shared=false', '&shared=false')
                : url + '&shared=false';
            const sharedUrl = url.includes('shared=') 
                ? url 
                : url + '&shared=true';
            
            // Add is_add_mode parameter to both URLs
            const ownUrlWithMode = ownUrl.includes('?') ? ownUrl + '&is_add_mode=true' : ownUrl + '?is_add_mode=true';
            const sharedUrlWithMode = sharedUrl.includes('?') ? sharedUrl + '&is_add_mode=true' : sharedUrl + '?is_add_mode=true';
            
            // Fetch both own and shared collections
            const [ownResponse, sharedResponse] = await Promise.all([
                axios.get(ownUrlWithMode),
                axios.get(sharedUrlWithMode)
            ]);
            
            const ownSpaces = ownResponse.data.spaces || [];
            const sharedSpaces = sharedResponse.data.spaces || [];
            
            // Combine both lists, ensuring no duplicates
            const combinedSpaces = [...ownSpaces];
            const ownSpaceIds = new Set(ownSpaces.map(space => space.id));
            
            sharedSpaces.forEach(sharedSpace => {
                if (!ownSpaceIds.has(sharedSpace.id)) {
                    combinedSpaces.push(sharedSpace);
                }
            });
            
            const newSpacesList = combinedSpaces;
            
            if (isSearch) {
                // For search operations, only update the searchable spaces list
                // Keep the original list intact and preserve selected spaces from original list
                setSpacesList(newSpacesList);
                
                if (preserveSelected && selectedSpaces.length > 0) {
                    // Find selected spaces in the original list (not search results)
                    const findSpaceInOriginalTree = (spaces, targetId) => {
                        for (const space of spaces) {
                            if (space.id === targetId) {
                                return space;
                            }
                            if (space.childrens && space.childrens.length > 0) {
                                const found = findSpaceInOriginalTree(space.childrens, targetId);
                                if (found) return found;
                            }
                        }
                        return null;
                    };
                    
                    // Preserve selected spaces by finding them in the original list
                    const preservedSelectedSpaces = selectedSpaces.map(selectedSpace => {
                        const foundSpace = findSpaceInOriginalTree(originalSpacesList, selectedSpace.id);
                        return foundSpace || selectedSpace; // Fallback to original if not found
                    }).filter(Boolean);
                    
                    setSelectedSpaces(preservedSelectedSpaces);
                }
            } else {
                // For non-search operations (initial load, clear form), update both lists
                setSpacesList(newSpacesList);
                setOriginalSpacesList(newSpacesList);
                
                if (preserveSelected && selectedSpaces.length > 0) {
                    // Create a function to find a space by ID in the new tree
                    const findSpaceInNewTree = (spaces, targetId) => {
                        for (const space of spaces) {
                            if (space.id === targetId) {
                                return space;
                            }
                            if (space.childrens && space.childrens.length > 0) {
                                const found = findSpaceInNewTree(space.childrens, targetId);
                                if (found) return found;
                            }
                        }
                        return null;
                    };
                    
                    // Preserve selected spaces by finding them in the new list
                    const preservedSelectedSpaces = selectedSpaces.map(selectedSpace => {
                        const foundSpace = findSpaceInNewTree(newSpacesList, selectedSpace.id);
                        return foundSpace || selectedSpace; // Fallback to original if not found
                    }).filter(Boolean);
                    
                    setSelectedSpaces(preservedSelectedSpaces);
                }
            }
            
            return newSpacesList;
        } catch (error) {
            console.error('Error fetching spaces:', error);
            throw error;
        }
    };

    // -----------------------------------------------
    // Search Handler
    // -----------------------------------------------
    const handleSearch = async () => {
        setIsSearching(true);
        try {
            let url;
            if (!searchQuery.trim()) {
                // If search is empty, call the initial endpoint without space_name
                url = `${API_URL}/spaces?minimal_mode=true`;
            } else {
                // If there's a search query, use the search endpoint
                url = `${API_URL}/spaces?space_name=${encodeURIComponent(searchQuery.trim())}&minimal_mode=true`;
            }
            
            await updateSpacesListWithPreservation(url, true, true);
        } catch (error) {
            console.error('Error searching spaces:', error);
        } finally {
            setIsSearching(false);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    useEffect(() => {
        // Only fetch spaces when the dialog is open and we don't already have spaces loaded
        if (open && spacesList.length === 0 && !spacesLoading) {
            setSpacesLoading(true);
            updateSpacesListWithPreservation(`${API_URL}/spaces?minimal_mode=true`, false, false)
                .then(() => {
                    setSpacesLoading(false);
                })
                .catch((err) => {
                    console.error(err);
                    setSpacesLoading(false);
                });
        }
    }, [open, spacesList.length, spacesLoading]); // Added dependencies to prevent unnecessary re-fetches

    // 3) Whenever the route or spaces change, parse out the SpaceID from the URL
    useEffect(() => {
        // Helper function to fetch parent's children and select the specific space
        const fetchAndSelectSpace = async (spaceId, parentSpaceIdParam) => {
            try {
                if (parentSpaceIdParam) {
                    // We have parent space ID, fetch parent's children directly
                    const parentResponse = await axios.get(`${API_URL}/spaces?space_id=${parentSpaceIdParam}&is_add_mode=true`);
                    const subspaces = parentResponse.data.spaces || [];
                    
                    // Update the parent's children in the tree
                    const updateSpaceChildren = (spaces, targetId, newChildren) => {
                        return spaces.map(s => {
                            if (s.id === targetId) {
                                return { ...s, childrens: newChildren };
                            }
                            if (s.childrens && s.childrens.length > 0) {
                                return { ...s, childrens: updateSpaceChildren(s.childrens, targetId, newChildren) };
                            }
                            return s;
                        });
                    };
                    
                    const updatedSpacesList = updateSpaceChildren(spacesList, parentSpaceIdParam, subspaces);
                    setSpacesList(updatedSpacesList);
                    setOriginalSpacesList(updatedSpacesList);
                    
                    // Find and select the specific space from the fetched children
                    const foundSpace = subspaces.find(s => s.id === spaceId);
                    if (foundSpace) {
                        setSelectedSpaces([foundSpace]);
                    }
                    
                    // Expand the parent
                    setExpandedSpaces(prev => ({
                        ...prev,
                        [parentSpaceIdParam]: true
                    }));
                } else {
                    // Fallback: fetch the space itself to get parent_id
                    const response = await axios.get(`${API_URL}/spaces/authenticated/${spaceId}`);
                    const spaceData = response.data;
                    
                    if (spaceData) {
                        setSelectedSpaces([spaceData]);
                        
                        // If the space has a parent, we should expand it in the tree
                        if (spaceData.parent_id) {
                            const parentResponse = await axios.get(`${API_URL}/spaces?space_id=${spaceData.parent_id}&is_add_mode=true`);
                            const subspaces = parentResponse.data.spaces || [];
                            
                            // Update the parent's children in the tree
                            const updateSpaceChildren = (spaces, targetId, newChildren) => {
                                return spaces.map(s => {
                                    if (s.id === targetId) {
                                        return { ...s, childrens: newChildren };
                                    }
                                    if (s.childrens && s.childrens.length > 0) {
                                        return { ...s, childrens: updateSpaceChildren(s.childrens, targetId, newChildren) };
                                    }
                                    return s;
                                });
                            };
                            
                            const updatedSpacesList = updateSpaceChildren(spacesList, spaceData.parent_id, subspaces);
                            setSpacesList(updatedSpacesList);
                            setOriginalSpacesList(updatedSpacesList);
                            
                            // Expand the parent
                            setExpandedSpaces(prev => ({
                                ...prev,
                                [spaceData.parent_id]: true
                            }));
                        }
                    }
                }
            } catch (error) {
                console.error('Error fetching specific space:', error);
            }
        };

        if (!spacesLoading && spacesList.length > 0) {
            // First check if we have a preselected space from AddToSpace
            if (preselectedSpaceId) {
                const foundSpace = findSpaceInTree(spacesList, preselectedSpaceId);
                if (foundSpace) {
                    setSelectedSpaces([foundSpace]);
                    return;
                } else {
                    // Space not found in tree, fetch parent's children
                    fetchAndSelectSpace(preselectedSpaceId, preselectedParentSpaceId);
                    return;
                }
            }
            
            // Otherwise, check URL for space ID
            const match = location.pathname.match(/\/spaces\/([^/]+)/);
            if (match && match[1]) {
                const foundSpace = findSpaceInTree(spacesList, match[1]);
                if (foundSpace) {
                    setSelectedSpaces([foundSpace]);
                } else {
                    // Space not found in tree, fetch it directly
                    fetchAndSelectSpace(match[1], null);
                }
            } else {
                // If not on /spaces/... path, either keep existing or clear selection
                setSelectedSpaces([]);
            }
        }
    }, [location, spacesLoading, spacesList, preselectedSpaceId, preselectedParentSpaceId]);

    // -----------------------------------------------
    // Like functionality for rated objects
    // -----------------------------------------------
    const authorized = useSelector(({ authorized }) => authorized);
    const [isLiked, setIsLiked] = useState(false);
    const [likeCount, setLikeCount] = useState(initialData?.likes || 0);
    const [isLiking, setIsLiking] = useState(false);

    const handleLikeRatedObject = async () => {
        if (!authorized || isLiking || !id) return;
        
        setIsLiking(true);
        try {
            const response = await toggleLike({ rated_object_id: id });
            setLikeCount(response.like_count || 0);
            setIsLiked(!isLiked);
        } catch (error) {
            if (error.message === "UNAUTHORIZED") {
                dispatch({ type: "SET_UNAUTHORIZED" });
            } else {
                dispatch({
                    type: "SET_POPUP",
                    payload: {
                        text: error?.response?.data?.error || "Failed to like. Please try again.",
                        type: "error",
                    },
                });
                setTimeout(() => {
                    dispatch({ type: "SET_POPUP", payload: { text: "", type: "" } });
                }, popupTimer);
            }
        } finally {
            setIsLiking(false);
        }
    };

    // -----------------------------------------------
    // Submission & Loading
    // -----------------------------------------------
    const [isSubmitting, setIsSubmitting] = useState(false);

    const clearForm = () => {
        handleClose();
        setRatingObject(0);
        setTitle("");
        setComment("");
        setTags([]);
        setLinkUrl("");
        setIsPublic(false);
        setDisplayedImage(null);
        setStagedImageFile(null);
        setIsDragging(false);
        setSelectedSpaces([]);
        setSearchQuery("");
        setShowSpaceSelection(false);
        // Reset both spaces lists to initial state
        setSpacesLoading(true);
        updateSpacesListWithPreservation(`${API_URL}/spaces?minimal_mode=true`, false, false)
            .then(() => {
                setSpacesLoading(false);
            })
            .catch((err) => {
                console.error(err);
                setSpacesLoading(false);
            });
    };

    // Simple close function that doesn't clear everything
    const simpleClose = () => {
        handleClose();
    };

    // Handle image drop & drag events
    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        if (e.dataTransfer.files?.[0]) {
            handleImageSelection(e.dataTransfer.files[0]);
        }
    };
    const handleDragOver = (e) => {
        e.preventDefault();
        setIsDragging(true);
    };
    const handleDragLeave = () => {
        setIsDragging(false);
    };
    const handleFileInput = (e) => {
        if (e.target.files?.[0]) {
            handleImageSelection(e.target.files[0]);
        }
    };

    // Convert selected image file to base64
    const handleImageSelection = (file) => {
        if (file.type?.startsWith("image/")) {
            const reader = new FileReader();
            reader.onload = (ev) => {
                setDisplayedImage(ev.target.result);
                setStagedImageFile(file);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleCancelImage = () => {
        setDisplayedImage(null);
        setStagedImageFile(null);
    };
    const handleRemoveImage = () => {
        setDisplayedImage(null);
        setStagedImageFile(null);
    };
    const handleCropAgain = () => {
        /* If re-cropping is needed, implement here */
    };
    const handleSaveImage = () => {
        /* If you do a "Crop" apply, handle that here */
    };

    // -----------------------------------------------
    // Form Validation
    // -----------------------------------------------
    const isFormValid = () => {
        // Feedback mode requires at least a comment
        if (isFeedback) {
            return comment.trim().length > 0;
        }
        // For rating, stars are NOT required anymore; title is optional
        return true;
    };

    // -----------------------------------------------
    // Submit
    // -----------------------------------------------
    const addRate = () => {
        if (!isFormValid() || isSubmitting) return;
        setIsSubmitting(true);

        // Build request body
        const requestBody = {
            metadata: {
                page_id: { href: selectedObjectId || id },
                page_url: linkUrl,
                css_path: "",
            },
            comment,
            title,
            rate: {
                rate_points: ratingObject,
                tags,
                spaces: selectedSpaces.map(s => s.id)
            },
            image: displayedImage || "",
            rated_object_id: selectedObjectId || id,
            is_public: isPublic
        };


        axios
            .post(`${API_URL}/rates/authenticated`, requestBody, {
                headers: {
                    "Content-Type": "application/json",
                },
            })
            .then(() => {
                dispatch({ type: "GET_RATES", payload: true });

                dispatch({
                    type: "SET_POPUP",
                    payload: {
                        text: isFeedback
                            ? "Feedback Submitted Successfully"
                            : "Rating Added Successfully",
                        type: "success",
                    },
                });
                setTimeout(() => {
                    dispatch({ type: "SET_POPUP", payload: { text: "", type: "" } });
                }, popupTimer);

                if (onRateSuccessfully) {
                    onRateSuccessfully(rateId || id);
                }
                clearForm();
            })
            .catch((err) => {
                if (err?.response?.status === 401) {
                    dispatch({ type: "SET_UNAUTHORIZED" });
                    dispatch({
                        type: "SET_POPUP",
                        payload: {
                            text: "You must Login to Perform this Action",
                            type: "error",
                        },
                    });
                    setTimeout(() => {
                        dispatch({ type: "SET_POPUP", payload: { text: "", type: "" } });
                    }, popupTimer);
                    clearForm();
                    return;
                }
            })
            .finally(() => {
                setIsSubmitting(false);
            });
    };

    // Handle space expansion/collapse
    const handleSpaceToggle = async (spaceId) => {
        const space = findSpaceInTree(spacesList, spaceId);
        if (!space || !space.has_children) return;

        try {
            setLoadingSubspaces(prev => ({ ...prev, [spaceId]: true }));
            
            const response = await axios.get(`${API_URL}/spaces?space_id=${spaceId}`);
            const subspaces = response.data.spaces || [];

            // Update the space's children in both the searchable list and original list
            const updateSpaceChildren = (spaces, targetId, newChildren) => {
                return spaces.map(s => {
                    if (s.id === targetId) {
                        return { ...s, childrens: newChildren };
                    }
                    if (s.childrens && s.childrens.length > 0) {
                        return { ...s, childrens: updateSpaceChildren(s.childrens, targetId, newChildren) };
                    }
                    return s;
                });
            };

            const updatedSpacesList = updateSpaceChildren(spacesList, spaceId, subspaces);
            const updatedOriginalList = updateSpaceChildren(originalSpacesList, spaceId, subspaces);
            
            // Update both lists
            setSpacesList(updatedSpacesList);
            setOriginalSpacesList(updatedOriginalList);
            
            // Preserve selected spaces from the updated original list
            if (selectedSpaces.length > 0) {
                const findSpaceInUpdatedTree = (spaces, targetId) => {
                    for (const space of spaces) {
                        if (space.id === targetId) {
                            return space;
                        }
                        if (space.childrens && space.childrens.length > 0) {
                            const found = findSpaceInUpdatedTree(space.childrens, targetId);
                            if (found) return found;
                        }
                    }
                    return null;
                };
                
                const preservedSelectedSpaces = selectedSpaces.map(selectedSpace => {
                    const foundSpace = findSpaceInUpdatedTree(updatedOriginalList, selectedSpace.id);
                    return foundSpace || selectedSpace;
                }).filter(Boolean);
                
                setSelectedSpaces(preservedSelectedSpaces);
            }
            
        } catch (error) {
            console.error('Error fetching subspaces:', error);
        } finally {
            setLoadingSubspaces(prev => ({ ...prev, [spaceId]: false }));
        }

        setExpandedSpaces(prev => ({
            ...prev,
            [spaceId]: !prev[spaceId]
        }));
    };

    // Render a single space item with its children
    const renderSpaceItem = (space, level = 0) => {
        const isExpanded = expandedSpaces[space.id];
        const hasChildren = space.has_children || (space.childrens && space.childrens.length > 0);
        const isLoading = loadingSubspaces[space.id];
        const isSelected = selectedSpaces.some(s => s.id === space.id);

        return (
            <Box key={space.id}>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        pl: level * 3 + 1,
                        py: 0.75,
                        cursor: 'pointer',
                        ...spaceSelectionStyles.spaceItem,
                        ...(isSelected && spaceSelectionStyles.selectedSpaceItem),
                        borderRadius: 1,
                        mx: 0.5,
                        position: 'relative',
                        '&::before': level > 0 ? {
                            content: '""',
                            position: 'absolute',
                            left: level * 3 - 1,
                            top: '50%',
                            width: 12,
                            height: 1,
                            bgcolor: 'divider',
                            transform: 'translateY(-50%)'
                        } : {}
                    }}
                    onClick={() => {
                        if (isSelected) {
                            setSelectedSpaces(prev => prev.filter(s => s.id !== space.id));
                        } else {
                            setSelectedSpaces(prev => [...prev, space]);
                        }
                    }}
                >
                    {hasChildren && (
                        <Tooltip title={isExpanded ? "Collapse" : "Expand"}>
                            <IconButton
                                size="small"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleSpaceToggle(space.id);
                                }}
                                sx={{ 
                                    mr: 0.5,
                                    ml: -0.5,
                                    color: isExpanded ? 'primary.main' : 'text.secondary',
                                    '&:hover': {
                                        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1)
                                    }
                                }}
                            >
                                {isLoading ? (
                                    <CircularProgress size={16} />
                                ) : isExpanded ? (
                                    <FolderOpenIcon fontSize="small" />
                                ) : (
                                    <FolderIcon fontSize="small" />
                                )}
                            </IconButton>
                        </Tooltip>
                    )}
                    {!hasChildren && level > 0 && (
                        <Box sx={{ width: 24, display: 'flex', justifyContent: 'center' }}>
                            <Box 
                                sx={{ 
                                    width: 4, 
                                    height: 4, 
                                    borderRadius: '50%', 
                                    bgcolor: 'text.secondary',
                                    opacity: 0.5
                                }} 
                            />
                        </Box>
                    )}
                    <Typography
                        variant="body2"
                        sx={{
                            flex: 1,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            color: isSelected ? 'primary.main' : 'text.primary',
                            fontWeight: isSelected ? 500 : 400,
                            pl: hasChildren ? 0 : 1,
                            transition: 'all 0.2s ease'
                        }}
                    >
                        {space.name}
                    </Typography>
                </Box>
                <Collapse in={isExpanded} timeout={200}>
                    {space.childrens && space.childrens.map(child => 
                        renderSpaceItem(child, level + 1)
                    )}
                </Collapse>
            </Box>
        );
    };

    // Add this before the return statement
    const handleRemoveSpace = (spaceToRemove) => {
        setSelectedSpaces(prev => prev.filter(space => space.id !== spaceToRemove.id));
    };

    // -----------------------------------------------
    // Render
    // -----------------------------------------------
    return (
        <Modal
            open={open}
            onClose={simpleClose}
            closeAfterTransition
            aria-labelledby="rating-modal-title"
            aria-describedby="rating-modal-description"
        >
            <Fade in={open}>
                <Paper sx={modalStyle} elevation={24}>
                    {/* Sticky Header */}
                    <Box
                        sx={{
                            p: 2,
                            bgcolor: (theme) => theme.palette.primary.main,
                            color: "white",
                            borderTopLeftRadius: 12,
                            borderTopRightRadius: 12,
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            position: "sticky",
                            top: 0,
                            zIndex: 2,
                        }}
                    >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, flex: 1 }}>
                            <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
                                {isFeedback ? "Send Feedback" : "Add Rating"}
                            </Typography>
                            {initialData && id && (
                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                    <IconButton
                                        onClick={handleLikeRatedObject}
                                        disabled={isLiking || !authorized}
                                        size="small"
                                        sx={{
                                            color: isLiked ? "rgba(255, 255, 255, 0.9)" : "rgba(255, 255, 255, 0.7)",
                                            "&:hover": {
                                                color: "white",
                                                backgroundColor: "rgba(255, 255, 255, 0.1)",
                                            },
                                        }}
                                    >
                                        {isLiked ? (
                                            <FavoriteIcon fontSize="small" />
                                        ) : (
                                            <FavoriteBorderIcon fontSize="small" />
                                        )}
                                    </IconButton>
                                    {likeCount > 0 && (
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: "rgba(255, 255, 255, 0.9)",
                                                fontSize: "0.875rem",
                                                minWidth: "20px",
                                            }}
                                        >
                                            {likeCount}
                                        </Typography>
                                    )}
                                </Box>
                            )}
                        </Box>
                        <IconButton onClick={simpleClose} size="small" sx={{ color: "white" }}>
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    {/* Rating Stars (optional now) */}
                    {!isFeedback && (
                        <Box
                            sx={{
                                textAlign: "center",
                                padding: "22px 0 10px",
                                position: "sticky",
                                top: "64px",
                                zIndex: 1,
                                bgcolor: "background.paper"
                            }}
                        >
                            <Rating
                                style={{
                                    color: "rgba(255, 204, 72, 1)",
                                    background: "#F8F8F8",
                                    padding: "8px 24px",
                                    borderRadius: "25px",
                                }}
                                name="hover-feedback"
                                value={ratingObject}
                                onChange={(e) => setRatingObject(Number(e.target.value))}
                                emptyIcon={<StarIcon style={{ opacity: 0.55 }} fontSize="inherit" />}
                            />
                        </Box>
                    )}

                    {/* Scrollable Content */}
                    <Box
                        sx={{
                            p: 3,
                            pt: 2,
                            overflowY: "auto",
                            flexGrow: 1,
                            display: "flex",
                            flexDirection: "column",
                            pb: 10, // extra bottom padding
                        }}
                    >

                        {/* Share with Community Checkbox - First Action */}
                        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                            <Checkbox
                                checked={isPublic}
                                onChange={(e) => setIsPublic(e.target.checked)}
                                color="primary"
                                sx={{ mr: 1 }}
                            />
                            <Typography variant="body2" color="text.secondary">
                                Share this {isFeedback ? "feedback" : "review"} with the community
                            </Typography>
                        </Box>
                        <Divider sx={{ mb: 2 }} />

                        {/* Title Field (with icon) */}
                        <Box sx={{ display: "flex", alignItems: "flex-start", mb: 2 }}>
                            <DriveFileRenameOutlineIcon
                                sx={{ mt: 1, mr: 1, color: "text.secondary" }}
                            />
                            <TextField
                                variant="outlined"
                                size="small"
                                label="Title (Optional)"
                                placeholder="Title of your review..."
                                fullWidth
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                            />
                        </Box>
                        <Divider sx={{ mb: 2 }} />
                        {/* Optional Link URL */}
                        <Box sx={{ display: "flex", alignItems: "flex-start", mb: 2 }}>
                            <LinkIcon sx={{ mt: 1, mr: 1, color: "text.secondary" }} />
                            <TextField
                                variant="outlined"
                                size="small"
                                label="Optional Link"
                                placeholder="https://..."
                                fullWidth
                                value={linkUrl}
                                onChange={(e) => {
                                    const newValue = e.target.value.slice(0, INPUT_LIMITS.URL);
                                    setLinkUrl(newValue);
                                }}
                                inputProps={{
                                    maxLength: INPUT_LIMITS.URL
                                }}
                            />
                        </Box>
                        <Divider sx={{ mb: 2 }} />
                        {/* Comment / Description */}
                        <Box sx={{ display: "flex", alignItems: "flex-start", mb: 2 }}>
                            <CommentIcon sx={{ mt: 1, mr: 1, color: "text.secondary" }} />
                            <TextField
                                variant="outlined"
                                size="small"
                                label={isFeedback ? "Feedback" : "Comment"}
                                placeholder="Share your thoughts..."
                                fullWidth
                                multiline
                                rows={3}
                                value={comment}
                                onChange={(e) => {
                                    const newValue = e.target.value.slice(0, INPUT_LIMITS.COMMENT);
                                    setComment(newValue);
                                }}
                                inputProps={{
                                    maxLength: INPUT_LIMITS.COMMENT
                                }}
                                helperText={`${comment.length}/${INPUT_LIMITS.COMMENT} characters`}
                            />
                        </Box>
                        <Divider sx={{ mb: 2 }} />

                        <Stack spacing={3}>
                            {/* Tags (only if not feedback) */}
                            {!isFeedback && (
                                <Box sx={{ display: "flex", alignItems: "flex-start" }}>
                                    <TagIcon sx={{ mt: 2, mr: 1, color: "text.secondary" }} />
                                    <Autocomplete
                                        multiple
                                        fullWidth
                                        freeSolo
                                        options={[]}
                                        defaultValue={[]}
                                        value={tags}
                                        onChange={(e, newTags) => setTags(newTags)}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                variant="outlined"
                                                size="small"
                                                placeholder="Add Keywords"
                                                label="Tags"
                                                onBlur={(e) => {
                                                    const val = e.target.value.trim();
                                                    if (val !== "") {
                                                        setTags((prev) => [...prev, val]);
                                                    }
                                                }}
                                            />
                                        )}
                                    />
                                </Box>
                            )}
                            <Divider sx={{ mb: 2 }} />

                            {/* Selected Spaces Chips */}
                            {selectedSpaces.length > 0 && (
                                <Box sx={{ mb: 2 }}>
                                    <Typography 
                                        variant="subtitle2" 
                                        sx={{ 
                                            mb: 1, 
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            gap: 1,
                                            color: 'text.secondary'
                                        }}
                                    >
                                        Selected Spaces
                                        <Tooltip title={showSpaceSelection ? "Hide space selection" : "Show space selection"}>
                                            <IconButton
                                                size="small"
                                                onClick={() => setShowSpaceSelection(!showSpaceSelection)}
                                                sx={{ 
                                                    ml: 'auto',
                                                    color: showSpaceSelection ? 'primary.main' : 'text.secondary',
                                                    '&:hover': {
                                                        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1)
                                                    }
                                                }}
                                            >
                                                {showSpaceSelection ? <RemoveIcon fontSize="small" /> : <AddIcon fontSize="small" />}
                                            </IconButton>
                                        </Tooltip>
                                    </Typography>
                                    <Box sx={{ 
                                        display: 'flex', 
                                        flexWrap: 'wrap', 
                                        gap: 1,
                                        minHeight: 32,
                                        p: 0.5,
                                        borderRadius: 1,
                                        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04)
                                    }}>
                                        {selectedSpaces.map((space) => (
                                            <Chip
                                                key={space.id}
                                                label={space.name}
                                                onDelete={() => handleRemoveSpace(space)}
                                                deleteIcon={<DeleteOutlineIcon />}
                                                size="small"
                                                color="primary"
                                                variant="outlined"
                                                sx={spaceSelectionStyles.chip}
                                            />
                                        ))}
                                    </Box>
                                </Box>
                            )}

                            {/* Space Selection Section */}
                            <Box sx={{ 
                                display: "flex", 
                                width: "100%", 
                                mb: 3,
                                position: 'relative'
                            }}>
                                <Box sx={{ 
                                    position: 'absolute',
                                    left: 0,
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    zIndex: 1,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: 24,
                                    height: 24,
                                    color: "text.secondary"
                                }}>
                                    <MenuIcon sx={{ 
                                        fontSize: 20,
                                        flexShrink: 0
                                    }} />
                                </Box>
                                <Box sx={{ 
                                    flex: 1,
                                    pl: 3
                                }}>
                                    {selectedSpaces.length === 0 && !showSpaceSelection && (
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            onClick={() => setShowSpaceSelection(true)}
                                            sx={{ 
                                                width: '100%', 
                                                justifyContent: 'flex-start',
                                                color: 'text.secondary',
                                                borderColor: 'divider',
                                                height: 36,
                                                '&:hover': {
                                                    borderColor: 'primary.main',
                                                    color: 'primary.main',
                                                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04)
                                                }
                                            }}
                                        >
                                            Select collection(s)
                                        </Button>
                                    )}
                                    {(selectedSpaces.length > 0 || showSpaceSelection) && (
                                        <Box sx={{ 
                                            border: '1px solid', 
                                            borderColor: 'divider', 
                                            borderRadius: 1, 
                                            overflow: 'hidden',
                                            transition: 'all 0.2s ease',
                                            minHeight: 36,
                                            '&:hover': {
                                                borderColor: 'primary.main',
                                                boxShadow: (theme) => `0 0 0 1px ${alpha(theme.palette.primary.main, 0.2)}`
                                            }
                                        }}>
                                            <Box sx={{ 
                                                p: 1,
                                                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.02),
                                                borderBottom: '1px solid',
                                                borderColor: 'divider',
                                                minHeight: 36,
                                                display: 'flex',
                                                alignItems: 'center'
                                            }}>
                                                <Typography 
                                                    variant="subtitle2" 
                                                    sx={{ 
                                                        display: 'flex', 
                                                        alignItems: 'center', 
                                                        justifyContent: 'space-between',
                                                        color: 'text.secondary',
                                                        width: '100%'
                                                    }}
                                                >
                                                    Select Space(s)
                                                    <Tooltip title={showSpaceSelection ? "Hide space list" : "Show space list"}>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => setShowSpaceSelection(!showSpaceSelection)}
                                                            sx={{ 
                                                                color: showSpaceSelection ? 'primary.main' : 'text.secondary',
                                                                '&:hover': {
                                                                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1)
                                                                }
                                                            }}
                                                        >
                                                            {showSpaceSelection ? <RemoveIcon fontSize="small" /> : <AddIcon fontSize="small" />}
                                                        </IconButton>
                                                    </Tooltip>
                                                </Typography>
                                            </Box>
                                            <Collapse in={showSpaceSelection} timeout={200}>
                                                <Box sx={{ p: 1 }}>
                                                    {/* Search Box */}
                                                    <Box
                                                        sx={{
                                                            mb: 1,
                                                            backgroundColor: "#f5f5f5",
                                                            borderRadius: "8px",
                                                            padding: "4px 8px",
                                                            boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.1)",
                                                            width: "100%",
                                                            display: "flex",
                                                            alignItems: "center",
                                                        }}
                                                    >
                                                        <InputBase
                                                            placeholder="Search collections..."
                                                            value={searchQuery}
                                                            onChange={(e) => setSearchQuery(e.target.value)}
                                                            onKeyPress={handleKeyPress}
                                                            fullWidth
                                                            sx={{ 
                                                                fontSize: "14px",
                                                                color: "#333",
                                                            }}
                                                        />
                                                        <IconButton 
                                                            onClick={handleSearch}
                                                            disabled={isSearching}
                                                            size="small"
                                                            sx={{ 
                                                                p: 0.5,
                                                                '&:hover': {
                                                                    backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                                                }
                                                            }}
                                                        >
                                                            {isSearching ? (
                                                                <CircularProgress size={20} />
                                                            ) : (
                                                                <SearchIcon sx={{ 
                                                                    fontSize: 20, 
                                                                    color: "rgba(0, 0, 0, 0.6)",
                                                                }} />
                                                            )}
                                                        </IconButton>
                                                    </Box>
                                                    
                                                    {spacesLoading ? (
                                                        <Box sx={{ p: 2, textAlign: 'center' }}>
                                                            <CircularProgress size={24} />
                                                        </Box>
                                                    ) : (
                                                        <Box sx={{ 
                                                            maxHeight: 200, 
                                                            overflow: 'auto',
                                                            '&::-webkit-scrollbar': {
                                                                width: '8px',
                                                                height: '8px'
                                                            },
                                                            '&::-webkit-scrollbar-track': {
                                                                bgcolor: 'transparent'
                                                            },
                                                            '&::-webkit-scrollbar-thumb': {
                                                                bgcolor: (theme) => alpha(theme.palette.text.primary, 0.1),
                                                                borderRadius: '4px',
                                                                '&:hover': {
                                                                    bgcolor: (theme) => alpha(theme.palette.text.primary, 0.2)
                                                                }
                                                            }
                                                        }}>
                                                            {spacesList.map(space => renderSpaceItem(space))}
                                                        </Box>
                                                    )}
                                                </Box>
                                            </Collapse>
                                        </Box>
                                    )}
                                </Box>
                            </Box>


                            {/* Image Uploader */}
                            <Box style={{ marginLeft: "180px" }}>
                                <ImageUploaderAndCrop
                                    isDragging={isDragging}
                                    handleDrop={handleDrop}
                                    handleDragOver={handleDragOver}
                                    handleDragLeave={handleDragLeave}
                                    displayedImage={displayedImage}
                                    stagedImageFile={stagedImageFile}
                                    handleFileInput={handleFileInput}
                                    handleCancelImage={handleCancelImage}
                                    handleSaveImage={handleSaveImage}
                                    onRemoveImage={handleRemoveImage}
                                    handleCropAgain={handleCropAgain}
                                    isEditing={!!stagedImageFile}
                                    disabled={false}
                                />
                            </Box>
                        </Stack>
                    </Box>

                    {/* Sticky Footer */}
                    <Box
                        sx={{
                            p: 3,
                            borderTop: "1px solid",
                            borderColor: "divider",
                            bgcolor: "background.paper",
                            position: "fixed",
                            bottom: 0,
                            left: "50%",
                            transform: "translateX(-50%)",
                            width: "inherit",
                            maxWidth: "inherit",
                            display: "flex",
                            justifyContent: "flex-end",
                            borderBottomLeftRadius: 12,
                            borderBottomRightRadius: 12,
                            zIndex: 2,
                        }}
                    >
                        <Button variant="outlined" onClick={simpleClose} sx={{ mr: 2 }}>
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            onClick={addRate}
                            disabled={!isFormValid() || isSubmitting || spacesLoading}
                            startIcon={<SendIcon />}
                        >
                            {isSubmitting ? "Sending..." : "Submit"}
                        </Button>
                    </Box>
                </Paper>
            </Fade>
        </Modal>
    );
};

export default RatingObject;
